import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useStore } from '@store/StoreProvider'
import { Link, useNavigate } from 'react-router-dom'
import { toastStore } from '@store/ToastStore'
import { t } from '@store/I18nStore'

export default function ActivationPage() {
  const { i18n } = useStore();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [activationToken, setActivationToken] = useState('');
  const hasValidated = React.useRef(false);

  useEffect(() => {
    // Prevent duplicate calls in React Strict Mode
    if (hasValidated.current) return;
    hasValidated.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      toastStore.show(t('errors.auth.token_not_found'), "error");
      setIsValidatingToken(false);
      return;
    }

    setActivationToken(token);
    validateToken(token);
  }, []);

  // Validate token on page load
  const validateToken = async (token: string) => {
    setIsValidatingToken(true);
    try {
      const base = (i18n.apiBaseUrl || '').replace(/\/$/, '');
      const response = await fetch(`${base}/api/team/validate-activation-token?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        const errorMessage = data.message || data.error || t('errors.auth.link_token_invalid');
        toastStore.show(errorMessage, "error");
      }
    } catch (error) {
      setTokenValid(false);
      toastStore.show(t('errors.auth.check_token'), "error");
      console.error('Token validation error:', error);
    } finally {
      setIsValidatingToken(false);
    }
  };

  const clearError = (field: string) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validatePasswordStrength = (password: string) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
    };
  };

  const handleSubmit = async () => {
    setErrors({});

    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Password validation
    if (!password) {
      newErrors.password = t('errors.auth.password_required');
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = t('errors.auth.password_min_length');
      isValid = false;
    } else {
      const passwordCheck = validatePasswordStrength(password);
      if (!passwordCheck.isValid) {
        const missing = [];
        if (!passwordCheck.hasUpperCase) missing.push(t('auth.password_rules.uppercase'));
        if (!passwordCheck.hasLowerCase) missing.push(t('auth.password_rules.lowercase'));
        if (!passwordCheck.hasNumber) missing.push(t('auth.password_rules.digit'));
        newErrors.password = t('auth.password_rules.must_contain', { rules: missing.join(', ') });
        isValid = false;
      }
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.password.repeat');
      isValid = false;
    } else if (password && confirmPassword !== password) {
      newErrors.confirmPassword = t('errors.auth.passwords_mismatch');
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      toastStore.show(t('errors.fix_form'), "error");
      return;
    }

    // Make API call to activate account
    setIsLoading(true);
    try {
      const base = (i18n.apiBaseUrl || '').replace(/\/$/, '');
      const response: any = await fetch(`${base}/api/team/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: activationToken,
          password: password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toastStore.show(t('auth.success.registered'), "success");

        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 1500);

        // Clear form
        setPassword('');
        setConfirmPassword('');
      } else {
        // Handle specific error messages from API
        const errorMessage = data.message || data.error || t('errors.auth.create_account');
        toastStore.show(errorMessage, "error");

        // Handle specific field errors if API returns them
        if (data.errors) {
          const apiErrors: Record<string, string> = {};
          if (data.errors.password) apiErrors.password = data.errors.password;
          setErrors(apiErrors);
        }
      }
    } catch (error) {
      toastStore.show(t('errors.server_connection'), "error");
      console.error('Activation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
      }}>
        <div className="text-white text-xl">{t('auth.loading.check_token')}</div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex flex-col" style={{
        background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, Arial'
      }}>
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute w-[480px] h-[480px] rounded-full blur-[140px] opacity-35"
               style={{ background: 'rgba(100,60,255,.9)', top: '-120px', left: '-160px' }} />
          <div className="absolute w-[560px] h-[560px] rounded-full blur-[140px] opacity-35"
               style={{ background: 'rgba(60,100,255,.8)', bottom: '-180px', right: '-220px' }} />
        </div>

        <main className="flex-1 flex flex-col justify-center items-center px-5 py-10 relative z-10">
          <div className="text-center mb-6">
            <Link to="/" className="text-5xl mb-3">
              <img src="/assets/logo.svg" alt="logo" style={{width: '220px', margin: '0 auto 16px'}}/>
            </Link>
            <h1 className="text-3xl font-extrabold text-white mb-4">{t('errors.auth.invalid_link')}</h1>
            <p className="text-gray-300 mb-6">{t('errors.auth.activation_link_invalid')}</p>
            <Link to="/login" className="text-blue-500 font-semibold hover:text-purple-500 transition-colors">{t('auth.go_to_login_page')}</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, Arial'
    }}>
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[480px] h-[480px] rounded-full blur-[140px] opacity-35"
             style={{ background: 'rgba(100,60,255,.9)', top: '-120px', left: '-160px' }} />
        <div className="absolute w-[560px] h-[560px] rounded-full blur-[140px] opacity-35"
             style={{ background: 'rgba(60,100,255,.8)', bottom: '-180px', right: '-220px' }} />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col justify-center items-center px-5 py-10 relative z-10">
        <div className="text-center mb-6">
          <Link to="/" className="text-5xl mb-3">
            <img src="/assets/logo.svg" alt="logo" style={{width: '220px', margin: '0 auto 16px'}}/>
          </Link>
          <h1 className="text-3xl font-extrabold text-white">{t('auth.register.create_account_title')}</h1>
        </div>

        <div className="w-full max-w-[460px] p-8 rounded-3xl border"
             style={{
               background: 'rgba(18,10,58,.65)',
               borderColor: 'rgba(255,255,255,.08)',
               boxShadow: '0 16px 60px rgba(0,0,0,.55)'
             }}>
          <div>
            {/* Password field */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                  placeholder={t('auth.password.label')}
                  autoComplete="new-password"
                  minLength={8}
                  className={`w-full h-12 px-4 pr-14 rounded-xl text-white text-[15px] outline-none transition-all ${
                    errors.password ? 'border-2 border-red-500 shadow-[0_0_0_2px_rgba(255,85,102,.28)_inset]' : 'border border-white/20'
                  }`}
                  style={{ background: '#0e1232' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label={t('auth.password.show')}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={`min-h-[18px] text-[13px] text-red-300 mt-1.5 flex items-center gap-1.5 ${errors.password ? 'visible' : 'invisible'}`}>
                <span>⚠️</span>
                <span>{errors.password}</span>
              </div>
            </div>

            {/* Confirm Password field */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
                  placeholder={t('auth.password.repeat')}
                  autoComplete="new-password"
                  className={`w-full h-12 px-4 pr-14 rounded-xl text-white text-[15px] outline-none transition-all ${
                    errors.confirmPassword ? 'border-2 border-red-500 shadow-[0_0_0_2px_rgba(255,85,102,.28)_inset]' : 'border border-white/20'
                  }`}
                  style={{ background: '#0e1232' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label={t('auth.password.show')}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={`min-h-[18px] text-[13px] text-red-300 mt-1.5 flex items-center gap-1.5 ${errors.confirmPassword ? 'visible' : 'invisible'}`}>
                <span>⚠️</span>
                <span>{errors.confirmPassword}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full mt-2 py-3.5 rounded-xl border-0 font-bold text-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
              style={{ background: 'linear-gradient(90deg, #4f6ff5, #8a4fff)' }}
            >
              {isLoading ? t('auth.register.create_account_title') : t('auth.register.submit')}
            </button>

            <div className="flex justify-center items-center gap-1.5 mt-4 text-sm text-gray-300">
              <span>{t('auth.have_account')}</span>
              <Link to="/login" className="text-blue-500 font-semibold hover:text-purple-500 transition-colors">{t('app.actions.sign_in')}</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

